<template>
    <div class="markdown-viewer">
        <!-- Rendered Markdown Content -->
        <div v-if="source != null" 
            ref="markdownit" 
            class="markdown-container" 
            v-html="html" 
            @click="jackClicks"
        />

        <!-- Inject styles for markdown languages -->
        <v-style>
            {{language_block_styles}}
        </v-style>
    </div>
</template>

<script>
    var hljs = require('highlight.js'); // https://highlightjs.org/

    export default {
        props: ['source', 'current_page', 'driver'],
        name:"markdown",
        data() {
            return {
                markdown_ready: false,
                interval: null,
            }
        },

        methods: {
            // Override the default behaviour for clicking on links that lead within 
            //  TabNote, so that we can route them ourselves.
            jackClicks(event) {
                const matches_origin = event.target.origin === window.origin;
                const is_link = event.target.nodeName === 'A';
                if (is_link && matches_origin ) {
                    const href = event.target.getAttribute('href');
                    
                    this.$router.push(href)
                    
                    event.stopPropagation();
                    event.preventDefault();
                }
            },

        },
        

        created() {
            if (!window.markdown_wasm) {
                this.interval = setInterval(() => {
                    if (window.markdown_wasm) {
                        clearInterval(this.interval);
                        this.markdown_ready = true;
                    }
                }, 50);
            } else {
                this.markdown_ready = true;
            }
        },


        watch: {
            num_pages: {
                immediate: true,
                handler: function(pages) {
                    this.$emit("Ready", pages);
                }
            }
        },

        computed: {
            html() {
                // Return if the wasm markdown parser isn't ready.
                if (!this.markdown_ready) return `
                    <pre>Markdown loading...</pre>
                `;

                // Return if there's nothing to render;
                let page_index = Math.min(this.markdown_pages.length-1, this.current_page);
                if (!this.markdown_pages[page_index]) return '';
                
                const markdown_html = window.markdown_wasm.parse(this.markdown_pages[page_index]);
                const doc = new DOMParser().parseFromString(markdown_html, 'text/html');
                const body = doc.getElementsByTagName('body')[0];
                const links = body.getElementsByTagName('A');
                const code_blocks = body.querySelectorAll("pre>code[class]");

                // Add language labels to syntax highlighted blocks
                code_blocks.forEach(block => {
                    let label = document.createElement('div');
                    label.className= block.className + " label";
                    block.parentElement.insertBefore(label, block);
                    hljs.highlightBlock(block);
                });

                // Add 'internal' class to links that don't lead outside of tabnote:
                links.forEach(link => {
                    if (link.origin === window.origin) {
                        link.className = 'internal';
                    }
                })
                
                return body.innerHTML;
            },

            markdown_pages() {
                return this.source.split(/^----\n*?$/gm);
            },

            num_pages() {
                return this.markdown_pages.length;
            },

            languages() {
                let language_names = hljs.listLanguages();
                let languages = [];
                language_names.reduce(
                    (strings, lang) => {
                        let language = hljs.getLanguage(lang);
                        language.className = `language-${lang}`;
                    
                        languages.push(language);
                        return languages;
                    },
                    languages
                );

                return languages;    
            },

            language_block_styles() {
                return this.languages.reduce((style, language) => {
                    style = style + 
                    `pre>div.${language.className}.label::before {
                        content: "${language.name || language.className}";
                    }`;
                    return style;
                }, "pre>div.label::before { content: 'Preformatted';}\n");
            }
        },
    };
</script>

<style lang="scss" scoped >
    @import "../assets/colors.sass";

    $lighter-bg: lighten($dark-bg, 5%);
    .markdown-viewer {
        width: 100%;
        max-width: 100%;
        min-width: 100px;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        // padding-bottom: 25px;
    }

    .markdown-container {
        box-shadow: inset 0px 10px 15px #00000015;
        padding: 27px 20px;
        height: 100%;
        // border: 1px solid $border-medium;
        background-color: #292929FF;
        text-align: left;
        overflow-y: auto;
        word-break: break-word;
        font-family: Roboto Slab;

        &>*:last-child {
            margin-bottom: 25px
        }
    }

    .markdown-container /deep/ h1 {
        border-bottom:1px solid #f2f2f3;
        width:fit-content;
    }
    .markdown-container /deep/  {
        h1, h2, h3, h4, h5, h6, h7, h8 {
            margin-bottom: 0px;

        }
    }

    .markdown-container /deep/ p:first-child {
        margin-top: 0;
    }

    .markdown-container /deep/ table {
        width: 100%;
        border: 1px solid $border-medium;
        border-collapse: collapse;
        td, th {
            border: 1px solid $border-medium;
            margin: 0;
            padding: 5px;   
        }
    }

    .markdown-container  /deep/ pre {
        min-width: 0 !important;
        width: 100%;
        overflow: visible;
        margin: 12px 0;
        padding: 0;
        position: relative;
        * {
            font-family: Roboto Mono;
        }
        code {
            box-shadow: $mini-shadow;
            background-color: $lighter-bg;
            padding: 10px 5px 5px;
            display: block;
            border: $orange-dot-border;
            border-radius: 3px;
            width: 100%;
            height: 100%;
            overflow-x: auto;
            overflow-y: visible;
        }

        div.label {
            position: absolute;
            top: -10px;
            right: 8px;
            z-index: 10;
            overflow: hidden;

            height: 20px;
            padding: 2px 4px;
            font-size: 12px;
            
            background-color: $lighter-bg;
            color: #ababab;

            border: $orange-dot-border;
            border-radius: 3px;
            box-sizing: border-box;
            
        }
    }

    .markdown-container /deep/ {
        pre + pre {
            margin-top: 20px;
        }
    }
    .markdown-container /deep/ pre>div.language-no-highlight.label::before {
        content: "Preformatted";
    }
    .markdown-container  /deep/ ul {
        margin-top: 0;
        // margin-bottom: 0;
    }

    // Shrink margins between paragraphs
    .markdown-container /deep/ p {
        margin-top: 0;
        margin-bottom: 10px;
    }

    // fade stricken out text
    .markdown-container /deep/ s {
        color: #ababab;
    }

    // Remove space before lists
    .markdown-container /deep/ p+ul {
        margin-top: -8px;
    }
    // Remove space before lists
    .markdown-container /deep/ ul>ul {
        margin-bottom: 0px;
    }

    // Color `inline code`
    .markdown-container /deep/ code:not([class]) {
        border: $orange-dot-border;
        border-radius: 3px;
        padding: 0px 4px;
        background-color: $lighter-bg;
        font-family: Roboto Mono;
    }

    // Color links
    .markdown-container /deep/ a {
        color: $accent-bg;

        &.internal {
            font-family: Roboto Mono
        }
    }

    // TODO: Ensure that images are full width on mobile, and offer zooming options.
    // Fix Image sizes
    .markdown-container /deep/ img {
        max-width: 30%;
        transition: max-width 0.5s;
        &:hover {
            max-width: 100%;
        }
    }


    // Block quote
    .markdown-container /deep/ blockquote {
        position: relative;
        margin: 0;
        margin-left: 10px;
        margin-bottom: 10px;
        background-color: #fff1;
        padding: 5px;
        box-shadow: $mini-shadow;
  
        &::before {
            height: calc(100%);
            width: 3px;
            background-color: #fff1;
            position: absolute;
            left: -10px; 
            margin-top: -5px;
            content: " ";
            box-shadow: $mini-shadow;

        }

        &> p:last-child {
            margin-bottom: 0;
        }
    }

</style>
